import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  ByTermKeys,
  MonthDay,
  OnceKeys
} from '@components/payment-collection/constants';
import { OrgStatus } from '@constants/generic';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { PaymentCollectionType } from '@features/group-management/constants';
import {
  formatDueDatePlaceholder,
  getPaymentCollectionComponentType,
  getPaymentCollectionType
} from '@features/group-management/helper';
import { getDueDateTooltipText } from '@features/group-management/helpers/createGroup';
import { useCheckMemberPaymentLinkSentMutation } from '@features/group-management/queries/GroupQuery';
import {
  FormDataType,
  MemberListType,
  PaymentCollectionArg,
  PaymentCollectionComponentType,
  ValidationError
} from '@types';
import { formatISODate } from '@utils/date';
import { getLocalStorageItem } from '@utils/storage';

interface UseLeadGroupDateSettingsProps {
  formData: FormDataType;
  groupId: string;
  startDate: string;
  setStartDate: (value: string) => void;
  onInputChange: (fieldName: string, value: any) => void;
  error?: ValidationError;
  setError?: (value: ValidationError) => void;
  isEditGroup: boolean;
  memberList?: MemberListType[];
  memberId?: string;
}

const useLeadGroupDateSettings = ({
  formData,
  groupId,
  startDate,
  setStartDate,
  onInputChange,
  error,
  setError,
  isEditGroup,
  memberList,
  memberId
}: UseLeadGroupDateSettingsProps) => {
  const { currentOrg } = useSelector((state: any) => state.user);

  // --- Modal State ---
  const [isPaymentCollectionModalOpen, setIsPaymentCollectionModalOpen] =
    useState<boolean>(false);
  const [isStartDateModalOpen, setIsStartDateModalOpen] =
    useState<boolean>(false);
  const [isEndDateModalOpen, setIsEndDateModalOpen] = useState<boolean>(false);
  const [paymentCollectionFrequency, setPaymentCollectionFrequency] =
    useState<string>(PaymentCollectionType.MONTHLY);
  const [showConsent, setShowConsent] = useState<boolean>(false);

  // --- API ---
  const [checkPaymentLinkSent] = useCheckMemberPaymentLinkSentMutation();

  // --- Derived Values ---
  const isOrgLite =
    currentOrg?.status === OrgStatus.KYC_PENDING ||
    currentOrg?.status === OrgStatus.KYC_SUBMITTED;

  const dueDateTooltipText = getDueDateTooltipText(formData, isEditGroup);

  const isOnceGroup =
    formData.paymentCollectionType === PaymentCollectionType.ONCE ||
    paymentCollectionFrequency === PaymentCollectionType.ONCE;

  const isDueDateDisabled = !!formData.isCustomTerm || isOnceGroup;

  const isStartDateDisabled =
    paymentCollectionFrequency === PaymentCollectionType.ONCE ||
    (isEditGroup &&
      (!!formData.isCollectionStarted ||
        (!!formData.isCustomTerm &&
          !!formData.termsList &&
          formData.termsList.some((term) => term.isPaymentLinkSent)))) ||
    !!formData.isCustomTerm;

  const isEndDateDisabled =
    paymentCollectionFrequency === PaymentCollectionType.ONCE;

  const normalizedPaymentType = getPaymentCollectionType(
    formData.paymentCollectionType,
    Number(formData.intervalFrequency)
  );

  const dueDateValue =
    formData.activationDate ||
    formData.isImmediatePayment ||
    formData.isCustomTerm
      ? (formatDueDatePlaceholder(
          normalizedPaymentType,
          Number(formData.intervalFrequency),
          formData.activationDate,
          formData.isImmediatePayment || formData.isCustomTerm
        ) as string)
      : formData.activationDate;

  const paymentCollectionComponentType =
    formData.activationDate || formData.isCustomTerm
      ? (getPaymentCollectionComponentType(
          formData.paymentCollectionType,
          Number(formData.intervalFrequency)
        ) as PaymentCollectionComponentType)
      : PaymentCollectionComponentType.DEFAULT;

  // --- Helpers ---
  const formatDate = (date: string | Date) =>
    formatISODate({
      date: new Date(date),
      separator: '-',
      format: 'yyyy-mm-dd'
    });

  const endDateMinDate = () => {
    if (formData?.startDate) {
      if (new Date(formData?.startDate) < new Date()) return new Date();
      return new Date(formData?.startDate);
    }
    return new Date();
  };

  const endDateSelectedDate = () => {
    if (formData?.endDate) {
      return new Date(formData?.endDate);
    }
    if (formData?.startDate) {
      if (new Date(formData?.startDate) < new Date()) return new Date();
      return new Date(formData?.startDate);
    }
    return new Date();
  };

  // --- Handlers ---
  const checkDueConsent = async (data: PaymentCollectionArg) => {
    const getIntervalValue = () => {
      if (data.type === PaymentCollectionType.WEEKLY)
        return PaymentCollectionType.WEEKLY;
      if (data.type === PaymentCollectionType.ONCE)
        return PaymentCollectionType.ONCE;
      return PaymentCollectionType.MONTHLY;
    };
    const res = await checkPaymentLinkSent({
      params: {
        organizationId: getLocalStorageItem(LOCAL_STORAGE_KEYS.ORGANIZATION_ID),
        branchId: getLocalStorageItem(LOCAL_STORAGE_KEYS.BRANCH_ID),
        groupId: groupId || '',
        memberId: memberId || ''
      },
      body: {
        start_date: formatDate(startDate),
        interval_frequency: Number(data.interval),
        interval: getIntervalValue(),
        ...(getIntervalValue() === PaymentCollectionType.MONTHLY && {
          monthly_config: {
            day_option:
              // eslint-disable-next-line no-nested-ternary
              data.date === '1'
                ? MonthDay.FIRST
                : data.date === MonthDay.FIRST || data.date === MonthDay.LAST
                ? data.date
                : 'custom',
            ...(data.date !== MonthDay.FIRST &&
              data.date !== MonthDay.LAST &&
              data.date !== '1' && {
                custom_day: Number(data.date)
              })
          }
        }),
        ...(getIntervalValue() === PaymentCollectionType.WEEKLY && {
          weekly_config: {
            day_of_week: data.date
          }
        })
      }
    });
    if ('data' in res) {
      setShowConsent(res.data?.is_payment_link_sent || false);
    } else {
      setShowConsent(false);
    }
  };

  const onStartDateChange = (date: any) => {
    const formattedStartDate = formatDate(date);
    setStartDate(formattedStartDate);
    onInputChange('startDate', formattedStartDate);
    setIsStartDateModalOpen(false);
  };

  const onClearEndDate = () => {
    onInputChange('endDate', '');
    setError?.({ ...error, endDate: '' });
  };

  const onEndDateChange = (endDate: any) => {
    const formattedEndDate = formatDate(endDate);
    onInputChange('endDate', formattedEndDate);
    setIsEndDateModalOpen(false);
    const lastTerm = formData.termsList?.[formData.termsList.length - 1];
    const lastTermRefDate = lastTerm?.dueDate;
    if (
      endDate &&
      formData.isCustomTerm &&
      formData.termsList &&
      lastTermRefDate &&
      new Date(new Date(endDate).setHours(0, 0, 0, 0)) <
        new Date(new Date(lastTermRefDate).setHours(0, 0, 0, 0))
    ) {
      setError?.({
        ...error,
        endDate:
          "Deactivation date must be on or after the final term's due date"
      });
    } else {
      setError?.({ ...error, endDate: '' });
    }
  };

  const onDueDateChange = (data: PaymentCollectionArg) => {
    onInputChange('intervalFrequency', data.interval);
    onInputChange('paymentCollectionType', data.type);
    if (!isEditGroup) {
      memberList?.forEach((member) => {
        if (
          member.dueDate === formData.activationDate &&
          member.paymentCollectionType === formData.paymentCollectionType
        ) {
          if (
            data.date !== OnceKeys.IMMEDIATELY &&
            data.date !== ByTermKeys.ADVANCED
          )
            member.dueDate =
              data.type === 'once'
                ? formatDate(data.date)
                : (data.date as string);
          member.paymentCollectionType = data.type;
          member.interval = data.interval;
        }
      });
    }
    setPaymentCollectionFrequency(data.type);
    if (data.type === 'once' && data.date === OnceKeys.IMMEDIATELY) {
      onInputChange('isImmediatePayment', true);
      onInputChange('isCustomTerm', false);
    } else if (data.type === 'by_term' && data.date === ByTermKeys.ADVANCED) {
      onInputChange('isCustomTerm', true);
      onInputChange('isImmediatePayment', false);
    } else {
      onInputChange(
        'activationDate',
        data.type === 'once' ? formatDate(data.date) : data.date
      );
      onInputChange('isImmediatePayment', false);
      onInputChange('isCustomTerm', false);
    }
    if (isEditGroup && data.type !== PaymentCollectionType.ONCE)
      checkDueConsent(data);
    if (data.type === 'once') {
      onStartDateChange(new Date().toString());
      onClearEndDate();
    }
    setIsPaymentCollectionModalOpen(false);
  };

  const handleConsentConfirm = (data: any) => {
    setShowConsent(false);
    onInputChange('dueDateConsent', data);
  };

  // --- Effects ---
  useEffect(() => {
    if (formData.paymentCollectionType) {
      setPaymentCollectionFrequency(formData.paymentCollectionType);
    }
  }, [isEditGroup, formData.paymentCollectionType]);

  return {
    // Org info
    isOrgLite,
    dueDateTooltipText,

    // Disabled states
    isDueDateDisabled,
    isStartDateDisabled,
    isEndDateDisabled,
    isOnceGroup,

    // Due date values
    dueDateValue,
    normalizedPaymentType,
    paymentCollectionComponentType,

    // Modal states
    isPaymentCollectionModalOpen,
    setIsPaymentCollectionModalOpen,
    isStartDateModalOpen,
    setIsStartDateModalOpen,
    isEndDateModalOpen,
    setIsEndDateModalOpen,
    showConsent,
    setShowConsent,

    // Handlers
    onDueDateChange,
    onStartDateChange,
    onEndDateChange,
    onClearEndDate,
    handleConsentConfirm,

    // Date helpers
    endDateMinDate,
    endDateSelectedDate
  };
};

export default useLeadGroupDateSettings;
